(() => {
  'use strict';

  const registry = new Map();
  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);

  function emit(name, detail = {}) {
    const payload = { event: name, ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  function register(name, initializer) {
    registry.set(name, initializer);
  }

  function initComponent(root) {
    if (!(root instanceof Element) || instances.has(root)) return;
    const name = root.dataset.slComponent;
    const initializer = registry.get(name);
    if (!initializer) return;

    const destroy = initializer(root, {
      emit,
      reducedMotion: reducedMotion.matches,
      saveData
    });

    instances.set(root, typeof destroy === 'function' ? destroy : () => {});
  }

  function init(scope = document) {
    if (scope instanceof Element && scope.matches('[data-sl-component]')) {
      initComponent(scope);
    }
    scope.querySelectorAll?.('[data-sl-component]').forEach(initComponent);
  }

  function destroy(scope) {
    const targets = [];
    if (scope instanceof Element && scope.matches('[data-sl-component]')) targets.push(scope);
    scope.querySelectorAll?.('[data-sl-component]').forEach((node) => targets.push(node));
    targets.forEach((node) => {
      instances.get(node)?.();
      instances.delete(node);
    });
  }

  function initReveal(scope = document) {
    const targets = [...scope.querySelectorAll('.sl-reveal:not([data-sl-reveal-ready])')];
    if (!targets.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((node) => node.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    targets.forEach((node) => {
      node.dataset.slRevealReady = 'true';
      observer.observe(node);
    });
  }

  function initMedia(scope = document) {
    const videos = [...scope.querySelectorAll('video[data-sl-video]:not([data-sl-media-ready])')];
    if (!videos.length) return;

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting && !reducedMotion.matches && !saveData) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        }, { rootMargin: '120px 0px', threshold: .05 })
      : null;

    videos.forEach((video) => {
      video.dataset.slMediaReady = 'true';
      if (reducedMotion.matches || saveData) {
        video.pause();
        video.removeAttribute('autoplay');
      } else {
        observer?.observe(video);
      }

      video.addEventListener('error', () => {
        video.hidden = true;
        video.closest('.sl-media')?.classList.add('has-media-error');
      }, { once: true });
    });
  }

  function hydrate(scope = document) {
    init(scope);
    initReveal(scope);
    initMedia(scope);
  }

  register('tabs', (root, context) => {
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    if (!tabs.length || !panels.length) return;

    const activate = (tab, announce = true) => {
      tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = panel.id === tab.getAttribute('aria-controls');
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        panel.querySelectorAll('video').forEach((video) => {
          if (!active) video.pause();
          else if (!context.reducedMotion && !context.saveData) video.play().catch(() => {});
        });
      });
      if (announce) {
        context.emit('sl_scene_change', {
          section_id: root.dataset.sectionId,
          state: tab.dataset.state || tab.textContent.trim()
        });
      }
      root.dispatchEvent(new CustomEvent('sl:tabchange', {
        detail: { controls: tab.getAttribute('aria-controls') }
      }));
    };

    const onClick = (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab && tabs.includes(tab)) activate(tab);
    };

    const onKeydown = (event) => {
      const currentIndex = tabs.indexOf(document.activeElement);
      if (currentIndex < 0 || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      tabs[nextIndex].focus();
      activate(tabs[nextIndex]);
    };

    const onBlockSelect = (event) => {
      if (!root.contains(event.target)) return;
      const panel = event.target.closest('[role="tabpanel"]');
      if (!panel || !panels.includes(panel)) return;
      const tab = tabs.find((item) => item.getAttribute('aria-controls') === panel.id);
      if (tab) activate(tab, false);
    };

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeydown);
    document.addEventListener('shopify:block:select', onBlockSelect);
    activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0], false);

    return () => {
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKeydown);
      document.removeEventListener('shopify:block:select', onBlockSelect);
    };
  });

  register('spatial-navigator', (root, context) => {
    const panels = [...root.querySelectorAll('[data-sl-space-panel]')];
    const triggers = [...root.querySelectorAll('[data-sl-space-trigger]')];
    const previous = root.querySelector('[data-sl-space-prev]');
    const next = root.querySelector('[data-sl-space-next]');
    const counter = root.querySelector('[data-sl-space-counter]');
    if (!panels.length || !triggers.length) return;

    let activeIndex = 0;
    root.style.setProperty('--sl-space-count', String(panels.length));

    const activate = (requestedIndex, announce = true) => {
      const index = Math.max(0, Math.min(Number(requestedIndex), panels.length - 1));
      activeIndex = index;

      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
        panel.querySelectorAll('[data-sl-space-go]').forEach((hotspot) => {
          hotspot.tabIndex = active ? 0 : -1;
        });
      });

      triggers.forEach((trigger, triggerIndex) => {
        const active = triggerIndex === index;
        trigger.setAttribute('aria-selected', String(active));
        trigger.tabIndex = active ? 0 : -1;
      });

      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === panels.length - 1;
      if (counter) {
        counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(panels.length).padStart(2, '0')}`;
      }

      if (announce) {
        context.emit('sl_inspiration_space_change', {
          section_id: root.dataset.sectionId,
          state: panels[index].dataset.state || String(index + 1)
        });
      }
    };

    const onClick = (event) => {
      const trigger = event.target.closest('[data-sl-space-trigger]');
      if (trigger) {
        activate(Number(trigger.dataset.slSpaceTrigger));
        return;
      }
      const destination = event.target.closest('[data-sl-space-go]');
      if (destination) {
        activate(Number(destination.dataset.slSpaceGo));
        return;
      }
      if (event.target.closest('[data-sl-space-prev]')) activate(activeIndex - 1);
      if (event.target.closest('[data-sl-space-next]')) activate(activeIndex + 1);
    };

    const onKeydown = (event) => {
      const trigger = event.target.closest('[data-sl-space-trigger]');
      if (!trigger || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let targetIndex = Number(trigger.dataset.slSpaceTrigger);
      if (event.key === 'ArrowLeft') targetIndex = Math.max(0, targetIndex - 1);
      if (event.key === 'ArrowRight') targetIndex = Math.min(triggers.length - 1, targetIndex + 1);
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = triggers.length - 1;
      triggers[targetIndex].focus();
      activate(targetIndex);
    };

    const onBlockSelect = (event) => {
      if (!root.contains(event.target)) return;
      const panel = event.target.closest('[data-sl-space-panel]');
      const index = panels.indexOf(panel);
      if (index >= 0) activate(index, false);
    };

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeydown);
    document.addEventListener('shopify:block:select', onBlockSelect);
    activate(0, false);

    return () => {
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKeydown);
      document.removeEventListener('shopify:block:select', onBlockSelect);
    };
  });

  register('inspiration-concepts', (root, context) => {
    const filters = [...root.querySelectorAll('[data-sl-concept-filter]')];
    const concepts = [...root.querySelectorAll('[data-sl-concept]')];
    const dialog = root.querySelector('[data-sl-concept-dialog]');
    const dialogBody = root.querySelector('[data-sl-concept-dialog-body]');
    const close = root.querySelector('[data-sl-concept-close]');
    if (!filters.length || !concepts.length || !dialog || !dialogBody) return;

    let returnFocus = null;

    const applyFilter = (button) => {
      const selected = button.dataset.slConceptFilter;
      filters.forEach((filter) => filter.setAttribute('aria-pressed', String(filter === button)));
      concepts.forEach((concept) => {
        const tags = (concept.dataset.tags || '').split(',').filter(Boolean);
        concept.hidden = selected !== 'all' && !tags.includes(selected);
      });
      context.emit('sl_inspiration_filter', {
        section_id: root.dataset.sectionId,
        filter: selected
      });
    };

    const openStory = (button) => {
      const template = document.getElementById(button.dataset.slConceptOpen);
      if (!(template instanceof HTMLTemplateElement)) return;
      returnFocus = button;
      dialogBody.replaceChildren(template.content.cloneNode(true));
      const heading = dialogBody.querySelector('h3');
      if (heading) heading.id = `ConceptDialogTitle-${root.dataset.sectionId}`;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      context.emit('sl_inspiration_story_open', {
        section_id: root.dataset.sectionId,
        story: heading?.textContent.trim() || 'concept'
      });
    };

    const closeStory = () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      dialogBody.replaceChildren();
      returnFocus?.focus();
    };

    const onClick = (event) => {
      const filter = event.target.closest('[data-sl-concept-filter]');
      if (filter) {
        applyFilter(filter);
        return;
      }
      const opener = event.target.closest('[data-sl-concept-open]');
      if (opener) {
        openStory(opener);
        return;
      }
      if (event.target.closest('[data-sl-concept-close]')) closeStory();
    };

    const onBackdrop = (event) => {
      if (event.target === dialog) closeStory();
    };

    root.addEventListener('click', onClick);
    dialog.addEventListener('click', onBackdrop);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeStory();
    });
    applyFilter(filters.find((filter) => filter.getAttribute('aria-pressed') === 'true') || filters[0]);

    return () => {
      root.removeEventListener('click', onClick);
      dialog.removeEventListener('click', onBackdrop);
      if (dialog.open) closeStory();
    };
  });

  register('scroll-story', (root, context) => {
    const steps = [...root.querySelectorAll('[data-sl-story-step]')];
    const panels = [...root.querySelectorAll('[data-sl-story-panel]')];
    if (!steps.length || !panels.length) return;

    let activeIndex = -1;
    const activate = (index) => {
      if (index === activeIndex || !panels[index]) return;
      activeIndex = index;
      steps.forEach((step, stepIndex) => {
        step.classList.toggle('is-active', stepIndex === index);
        step.setAttribute('aria-current', stepIndex === index ? 'step' : 'false');
      });
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        panel.querySelectorAll('video').forEach((video) => {
          if (!active) video.pause();
          else if (!context.reducedMotion && !context.saveData) video.play().catch(() => {});
        });
      });
      context.emit('sl_scene_change', {
        section_id: root.dataset.sectionId,
        state: steps[index].dataset.state || String(index + 1)
      });
    };

    if (context.reducedMotion || !('IntersectionObserver' in window)) {
      activate(0);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      activate(steps.indexOf(visible.target));
    }, { rootMargin: '-36% 0px -44% 0px', threshold: [0, .2, .5, .8] });

    steps.forEach((step) => observer.observe(step));
    activate(0);
    return () => observer.disconnect();
  });

  register('video-toggle', (root) => {
    const button = root.querySelector('[data-sl-video-toggle]');
    const video = root.querySelector('video');
    if (!button || !video) return;

    const sync = () => {
      const paused = video.paused;
      button.setAttribute('aria-pressed', String(!paused));
      button.querySelector('[data-label]')?.replaceChildren(document.createTextNode(paused ? 'Play' : 'Pause'));
    };
    const onClick = () => {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
      sync();
    };
    button.addEventListener('click', onClick);
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    sync();

    return () => {
      button.removeEventListener('click', onClick);
      video.removeEventListener('play', sync);
      video.removeEventListener('pause', sync);
    };
  });

  let calendlyPromise;
  function loadCalendly() {
    if (window.Calendly) return Promise.resolve(window.Calendly);
    if (calendlyPromise) return calendlyPromise;
    calendlyPromise = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-sl-calendly]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://assets.calendly.com/assets/external/widget.css';
        link.dataset.slCalendly = 'true';
        document.head.appendChild(link);
      }
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.dataset.slCalendly = 'true';
      script.onload = () => resolve(window.Calendly);
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return calendlyPromise;
  }

  async function openMeeting(event, explicitTrigger) {
    const trigger = explicitTrigger || event.currentTarget || event.target.closest('[data-sl-meeting]');
    const url = trigger?.href || window.SanluceConfig?.calendlyUrl;
    if (!url) return;
    event.preventDefault();
    emit('sl_meeting_click', { location: trigger?.dataset.location || 'unknown' });
    try {
      const Calendly = await loadCalendly();
      Calendly.initPopupWidget({
        url,
        prefill: window.SanluceConfig?.customer || {}
      });
    } catch (_error) {
      window.location.assign(url);
    }
  }

  document.addEventListener('click', (event) => {
    const meeting = event.target.closest('[data-sl-meeting]');
    if (meeting) openMeeting(event, meeting);
    const vision = event.target.closest('[data-sl-vision]');
    if (vision) emit('sl_vision_click', { location: vision.dataset.location || 'unknown' });
  });

  document.addEventListener('focusin', (event) => {
    const form = event.target.closest('#SanluceProjectBrief');
    if (!form || form.dataset.slStarted) return;
    form.dataset.slStarted = 'true';
    emit('sl_form_start', { form: 'project_brief' });
  });

  document.addEventListener('submit', (event) => {
    if (!event.target.matches('#SanluceProjectBrief')) return;
    emit('sl_form_submit', { form: 'project_brief' });
  });

  document.addEventListener('DOMContentLoaded', () => hydrate(document), { once: true });
  document.addEventListener('shopify:section:load', (event) => hydrate(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:section:reorder', () => hydrate(document));

  window.Sanluce = {
    destroy,
    emit,
    hydrate,
    init,
    loadCalendly,
    openMeeting,
    register
  };
})();
